import { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter, matchPath } from 'react-router';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import CheckIcon from '@mui/icons-material/Check';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Avatar from '@mui/material/Avatar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Title from './Title';
import Invitation from './Invitation';
import routes from './routes';
import { login, logout, openDrawer, selectOrganization } from './actions';
import {
  getIsAuthenticated,
  getOrganizations,
  getSelectedOrganization,
  getUserInfo,
  getUserInvites,
  selectFacilityById,
} from './selectors';
import { Typography } from '@mui/material';

const PREFIX = 'NavBar';

const classes = {
  root: `${PREFIX}-root`,
  menuButton: `${PREFIX}-menuButton`,
  title: `${PREFIX}-title`,
  toolbar: `${PREFIX}-toolbar`,
  appBar: `${PREFIX}-appBar`,
  indicator: `${PREFIX}-indicator`,
  tabRoot: `${PREFIX}-tabRoot`,
  tab: `${PREFIX}-tab`,
  menu: `${PREFIX}-menu`,
  avatar: `${PREFIX}-avatar`,
  menuHeader: `${PREFIX}-menuHeader`,
  divider: `${PREFIX}-divider`,
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  [`& .${classes.root}`]: {
    flexGrow: 1,
  },

  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(0),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
    color: 'white',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  [`& .${classes.toolbar}`]: {
    //paddingRight: 24, // keep right padding when drawer closed
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('lg')]: {
      paddingLeft: theme.spacing(2),
    },
  },

  [`&.${classes.appBar}`]: {
    boxShadow: 'none',
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },

  [`& .${classes.indicator}`]: {
    backgroundColor: 'white',
  },

  [`& .${classes.tabRoot}`]: {
    backgroundColor: theme.palette.application.blue[800],
  },

  [`& .${classes.tab}`]: {
    fontSize: '0.7rem',
    // [theme.breakpoints.up('sm')]: {
    //   minWidth: 130,
    //   width: 130,
    // },
  },

  [`& .${classes.menu}`]: {
    '& .MuiListItemIcon-root': {
      minWidth: 36,
    },
    '& .MuiListItemText-inset': {
      paddingLeft: 36,
    },
  },

  [`& .${classes.avatar}`]: {
    backgroundColor: 'white',
  },

  [`& .${classes.divider}`]: {
    marginTop: theme.spacing(1),
  },
}));

const MenuHeader = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  fontSize: '1.1rem',
  fontWeight: theme.typography.fontWeightMedium,
}));

const drawerWidth = 220;

const AntTab = styled((props) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    minWidth: 0,
    [theme.breakpoints.up('sm')]: {
      minWidth: 0,
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: 0,
    color: 'rgba(255, 255, 255, 0.82)',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
      color: 'white',
      backgroundColor: theme.palette.application.blue[700],
      opacity: 1,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      fontWeight: 700,
      borderBottom: '2px solid white',
      position: 'relative',
      zIndex: 1,
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#d1eaff',
    },
  })
);

const AntTabs = styled((props) => <Tabs {...props} />)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  '& .MuiTabs-indicator': {
    backgroundColor: 'white',
  },
}));

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function NavBar(props) {
  const history = useHistory();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const user = useSelector(getUserInfo);
  const organizations = useSelector(getOrganizations);
  const organization = useSelector(getSelectedOrganization);
  const invites = useSelector(getUserInvites);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElOrg, setAnchorElOrg] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUserClick = () => {
    const userId = user.user_id.replace('auth0|', '');
    history.push(`/user/${userId}`);
  };

  const handleClickOrgMenu = (event) => {
    setAnchorElOrg(event.currentTarget);
  };

  const handleCloseOrg = () => {
    setAnchorElOrg(null);
  };

  const dispatch = useDispatch();
  const handleDrawerOpen = () => {
    dispatch(openDrawer());
  };

  const switchOrg = (orgId) => {
    if (orgId === organization.org_id) {
      handleCloseOrg();
    } else {
      dispatch(selectOrganization(orgId));
      history.push('/');
      handleCloseOrg();
    }
  };

  const siteRoutes = routes.find((r) => r.title === 'Facility').routes;
  const activeRoute = routes
    .concat(siteRoutes)
    .find((r) =>
      matchPath(props.location.pathname, { path: r.path, exact: true })
    );

  const match = matchPath(props.location.pathname, {
    path: activeRoute?.path,
    exact: true,
  });

  const facilityId = match?.params?.facilityId;
  const getActiveFacility = useMemo(
    () => selectFacilityById(facilityId),
    [facilityId]
  );
  const facility = useSelector(getActiveFacility);
  let title = activeRoute?.title;
  if (facility) {
    title = facility.facilityDesc;
  }

  if (!title && props.location.pathname.indexOf('docs') >= 0) {
    title = 'Help';
  }

  if (!title && props.location.pathname.indexOf('reports') >= 0) {
    title = 'Report';
  }

  if (title === 'System') {
    title = organization?.org_nm || 'System';
  }

  const isMonitoringPoint = facility?.facilityType === 'monitoring_point';
  const monitoringPointTabs = ['Status', 'Activity'];
  const visibleTabs = siteRoutes
    .filter((route) => route.tab)
    .filter(
      (route) => !isMonitoringPoint || monitoringPointTabs.includes(route.title)
    );

  const currentTab = Math.max(
    visibleTabs.findIndex((route) =>
      matchPath(props.location.pathname, {
        path: route?.path,
        exact: true,
      })
    ),
    0
  );

  return (
    <StyledAppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerOpen}
          className={classes.menuButton}
          size="large"
        >
          <MenuIcon />
        </IconButton>
        <Title className={classes.title}>{title}</Title>
        {!isAuthenticated && (
          <Button variant="contained" color="primary" onClick={login}>
            Login
          </Button>
        )}
        {!!organization && (
          <div>
            <Button
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleClickOrgMenu}
            >
              <Badge
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                style={{ color: 'white' }}
                badgeContent={<ExpandMore />}
              >
                <Avatar className={classes.avatar}>
                  <img
                    src={organization.org_logo}
                    alt={organization.org_nm}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Avatar>
              </Badge>
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={anchorElOrg}
              keepMounted
              open={Boolean(anchorElOrg)}
              onClose={handleCloseOrg}
              className={classes.menu}
            >
              <MenuItem
                onClick={() =>
                  history.push(`/organization/${organization.org_id}`)
                }
              >
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                Organization Settings
              </MenuItem>
              {organizations.length > 1 && (
                <Divider className={classes.divider} />
              )}
              {organizations.length > 1 && (
                <MenuHeader>Select Organization</MenuHeader>
              )}
              {organizations.length > 1 &&
                organizations.map((o) => (
                  <MenuItem
                    key={o.org_id}
                    selected={o.selected}
                    onClick={() => switchOrg(o.org_id)}
                  >
                    {o.selected && (
                      <ListItemIcon>
                        <CheckIcon />
                      </ListItemIcon>
                    )}
                    <ListItemText inset={!o.selected}> {o.org_nm}</ListItemText>
                  </MenuItem>
                ))}
            </Menu>
          </div>
        )}
        {!!user && (
          <div>
            <Button
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleClick}
            >
              <Badge badgeContent={invites.length} color="error">
                <Badge
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  style={{ color: 'white' }}
                  badgeContent={<ExpandMore />}
                >
                  <Avatar alt={user.name} src={user.picture} />
                </Badge>
              </Badge>
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleUserClick}>{user.email}</MenuItem>
              <MenuItem onClick={logout}>Logout</MenuItem>
              {invites.map((invite) => (
                <MenuItem key={invite.rKey}>
                  <Invitation invite={invite} />
                </MenuItem>
              ))}
            </Menu>
          </div>
        )}
      </Toolbar>
      {!!facility && (
        <AntTabs
          value={currentTab}
          variant="scrollable"
          scrollButtons={false}
          aria-label="simple tabs example"
          classes={{
            root: classes.tabRoot1,
          }}
        >
          {visibleTabs
            .filter((route) => {
              if (!route.roles || !route.roles.length) {
                return true;
              }
              return (
                route.roles.filter((role) => user.roles.includes(role)).length >
                0
              );
            })
            .map((route, index) => (
              <AntTab
                key={route.path}
                label={route.title}
                wrapped
                to={
                  route.path.replace(':facilityId', facility.facilityId) +
                  (route.dataWindow ? props.location.search : '')
                }
                component={RouterLink}
                classes={{ root: classes.tab }}
                {...a11yProps(index)}
              />
            ))}
        </AntTabs>
      )}
    </StyledAppBar>
  );
}

export default withRouter(NavBar);
